import { collection, getDocs, doc, updateDoc, setDoc, getDoc, Timestamp, arrayUnion } from 'firebase/firestore';
import { db } from '../firebase';
import { PlayerStats, PlayerProfile } from '../types';

export const RANKING_CATEGORIES = [
  { id: 'longestWordLength', label: 'Palabra más larga', badge: 'Líder de Palabras' },
  { id: 'gamesPlayed', label: 'Más partidas jugadas', badge: 'Veterano del Tablero' },
  { id: 'socialInteractions', label: 'Más sociables', badge: 'Jugador Sociable' },
  { id: 'uniqueOpponents', label: 'Más adversarios distintos', badge: 'Cazador de Duelos' },
  { id: 'bestScore', label: 'Máxima puntuación', badge: 'Campeón de Puntuación' },
  { id: 'totalScore', label: 'Puntos acumulados', badge: 'Maestro Acumulador' },
];

export const updateGlobalRankings = async () => {
  try {
    // 1. Fetch all users
    const usersSnap = await getDocs(collection(db, 'users'));
    const userIds = usersSnap.docs.map(d => d.id);
    
    // 2. Fetch all global stats
    const allStats: Record<string, PlayerStats> = {};
    await Promise.all(userIds.map(async (uid) => {
      const statsSnap = await getDoc(doc(db, 'users', uid, 'stats', 'global'));
      if (statsSnap.exists()) {
        allStats[uid] = statsSnap.data() as PlayerStats;
      }
    }));

    // 3. Calculate top 10% thresholds
    const thresholds: Record<string, number> = {};
    
    RANKING_CATEGORIES.forEach(cat => {
      const values = Object.values(allStats)
        .map(s => (s as any)[cat.id] || 0)
        .filter(v => v > 0)
        .sort((a, b) => b - a);
      
      if (values.length > 0) {
        const topIndex = Math.max(0, Math.floor(values.length * 0.1) - 1);
        thresholds[cat.id] = values[topIndex];
      } else {
        thresholds[cat.id] = Infinity; // No one qualifies
      }
    });

    // 4. Save thresholds globally
    await setDoc(doc(db, 'system', 'rankings'), {
      thresholds,
      updatedAt: Timestamp.now(),
      playerCount: userIds.length
    });

    // 5. Update all users' badges based on thresholds
    for (const uid of userIds) {
      const stats = allStats[uid];
      if (!stats) continue;

      const earnedBadges: string[] = [];
      RANKING_CATEGORIES.forEach(cat => {
        const value = (stats as any)[cat.id] || 0;
        if (value >= thresholds[cat.id] && value > 0) {
          earnedBadges.push(cat.badge);
        }
      });

      if (earnedBadges.length > 0) {
        await updateDoc(doc(db, 'users', uid), {
          badges: arrayUnion(...earnedBadges)
        });
      }
    }

    return thresholds;
  } catch (error) {
    console.error("Error updating global rankings:", error);
    throw error;
  }
};

export const checkAndAssignBadges = async (userId: string) => {
  try {
    // 1. Get global thresholds
    const rankingSnap = await getDoc(doc(db, 'system', 'rankings'));
    if (!rankingSnap.exists()) return [];
    const { thresholds } = rankingSnap.data();

    // 2. Get user stats
    const statsSnap = await getDoc(doc(db, 'users', userId, 'stats', 'global'));
    if (!statsSnap.exists()) return [];
    const stats = statsSnap.data() as PlayerStats;

    // 3. Get current user profile to check existing badges
    const userSnap = await getDoc(doc(db, 'users', userId));
    if (!userSnap.exists()) return [];
    const user = userSnap.data() as PlayerProfile;
    const currentBadges = user.badges || [];

    // 4. Check for new badges
    const newBadges: string[] = [];
    RANKING_CATEGORIES.forEach(cat => {
      const value = (stats as any)[cat.id] || 0;
      if (value >= thresholds[cat.id] && value > 0 && !currentBadges.includes(cat.badge)) {
        newBadges.push(cat.badge);
      }
    });

    if (newBadges.length > 0) {
      await updateDoc(doc(db, 'users', userId), {
        badges: arrayUnion(...newBadges)
      });
      return newBadges;
    }
    return [];
  } catch (error) {
    console.error("Error checking badges:", error);
    return [];
  }
};
